import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import url from '../constants/config';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

const STATUS_COLORS = {
  400: '#f59e0b',
  401: '#f59e0b',
  403: '#ef4444',
  404: '#6b7280',
  500: '#dc2626',
};

const statusColor = (code) => STATUS_COLORS[code] || (code >= 500 ? '#dc2626' : code >= 400 ? '#f59e0b' : '#6b7280');

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('pl-PL', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const AdminErrorLogs = () => {
  const [items, setItems] = useState([]);
  const [pageInfo, setPageInfo] = useState({ page: 1, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const [filters, setFilters] = useState({
    statusCode: '',
    route: '',
    userEmail: '',
    from: '',
    to: '',
  });
  const [page, setPage] = useState(1);

  const fetchLogs = useCallback(async (currentFilters, currentPage) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: currentPage, limit: 50 };
      if (currentFilters.statusCode) params.statusCode = currentFilters.statusCode;
      if (currentFilters.route) params.route = currentFilters.route;
      if (currentFilters.userEmail) params.userEmail = currentFilters.userEmail;
      if (currentFilters.from) params.from = currentFilters.from;
      if (currentFilters.to) params.to = currentFilters.to;

      const res = await axios.get(`${url}/admin/error-logs`, {
        headers: getAuthHeader(),
        params,
      });
      setItems(res.data.items || []);
      setPageInfo(res.data.pageInfo || { page: 1, total: 0, totalPages: 1 });
    } catch (err) {
      setError('Failed to load error logs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(filters, page);
  }, [fetchLogs, filters, page]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ statusCode: '', route: '', userEmail: '', from: '', to: '' });
    setPage(1);
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '20px', fontSize: '22px', fontWeight: 700 }}>
        Error Logs
        {pageInfo.total > 0 && (
          <span style={{ marginLeft: '10px', fontSize: '14px', fontWeight: 400, color: '#6b7280' }}>
            ({pageInfo.total} total)
          </span>
        )}
      </h2>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status Code</label>
          <input
            type="number"
            placeholder="e.g. 500"
            value={filters.statusCode}
            onChange={e => handleFilterChange('statusCode', e.target.value)}
            style={{ width: '100px', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Route</label>
          <input
            type="text"
            placeholder="e.g. /api/players"
            value={filters.route}
            onChange={e => handleFilterChange('route', e.target.value)}
            style={{ width: '180px', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>User Email</label>
          <input
            type="text"
            placeholder="e.g. coach@school.edu"
            value={filters.userEmail}
            onChange={e => handleFilterChange('userEmail', e.target.value)}
            style={{ width: '200px', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>From</label>
          <input
            type="date"
            value={filters.from}
            onChange={e => handleFilterChange('from', e.target.value)}
            style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>To</label>
          <input
            type="date"
            value={filters.to}
            onChange={e => handleFilterChange('to', e.target.value)}
            style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
          />
        </div>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={handleClearFilters}
            style={{ padding: '6px 14px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', color: '#374151' }}
          >
            Clear filters
          </button>
        )}
        <button
          type="button"
          onClick={() => fetchLogs(filters, page)}
          style={{ padding: '6px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
        >
          Refresh
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: '#fee2e2', color: '#991b1b', borderRadius: '6px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No error logs found.</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Method</th>
                  <th style={thStyle}>Route</th>
                  <th style={thStyle}>Message</th>
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>IP</th>
                  <th style={{ ...thStyle, width: '40px' }} />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <React.Fragment key={item._id}>
                    <tr
                      style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer', background: expanded === item._id ? '#f0f9ff' : 'white' }}
                      onClick={() => setExpanded(prev => prev === item._id ? null : item._id)}
                    >
                      <td style={tdStyle}>{formatDate(item.createdAt)}</td>
                      <td style={tdStyle}>
                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '4px', background: statusColor(item.statusCode), color: '#fff', fontWeight: 600 }}>
                          {item.statusCode}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ fontWeight: 600, color: item.method === 'GET' ? '#2563eb' : item.method === 'POST' ? '#16a34a' : item.method === 'DELETE' ? '#dc2626' : '#7c3aed' }}>
                          {item.method}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.route}
                      </td>
                      <td style={{ ...tdStyle, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.errorMessage}
                      </td>
                      <td style={{ ...tdStyle, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.userEmail || <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                      <td style={{ ...tdStyle, color: '#6b7280', fontFamily: 'monospace', fontSize: '11px' }}>
                        {item.ip || '—'}
                      </td>
                      <td style={tdStyle}>
                        <span style={{ color: '#9ca3af', fontSize: '10px' }}>{expanded === item._id ? '▲' : '▼'}</span>
                      </td>
                    </tr>
                    {expanded === item._id && (
                      <tr style={{ background: '#f0f9ff', borderBottom: '1px solid #bae6fd' }}>
                        <td colSpan={8} style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div>
                              <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '11px', color: '#374151', textTransform: 'uppercase' }}>Full Error Message</div>
                              <div style={{ fontFamily: 'monospace', fontSize: '12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '4px', padding: '8px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                {item.errorMessage}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '11px', color: '#374151', textTransform: 'uppercase' }}>Request Body</div>
                              <div style={{ fontFamily: 'monospace', fontSize: '12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '4px', padding: '8px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: '150px', overflow: 'auto' }}>
                                {item.requestBody && Object.keys(item.requestBody).length > 0
                                  ? JSON.stringify(item.requestBody, null, 2)
                                  : <span style={{ color: '#9ca3af' }}>empty</span>
                                }
                              </div>
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '11px', color: '#374151', textTransform: 'uppercase' }}>User Agent</div>
                              <div style={{ fontSize: '12px', color: '#6b7280', wordBreak: 'break-all' }}>
                                {item.userAgent || '—'}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '11px', color: '#374151', textTransform: 'uppercase' }}>Role</div>
                              <div style={{ fontSize: '12px' }}>
                                {item.userRole || <span style={{ color: '#9ca3af' }}>—</span>}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pageInfo.totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '20px' }}>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                style={{ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: '6px', background: page <= 1 ? '#f9fafb' : 'white', cursor: page <= 1 ? 'not-allowed' : 'pointer', color: page <= 1 ? '#9ca3af' : '#374151' }}
              >
                Prev
              </button>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>
                Page {pageInfo.page} of {pageInfo.totalPages}
              </span>
              <button
                type="button"
                disabled={page >= pageInfo.totalPages}
                onClick={() => setPage(p => p + 1)}
                style={{ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: '6px', background: page >= pageInfo.totalPages ? '#f9fafb' : 'white', cursor: page >= pageInfo.totalPages ? 'not-allowed' : 'pointer', color: page >= pageInfo.totalPages ? '#9ca3af' : '#374151' }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '12px',
  color: '#374151',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '9px 12px',
  verticalAlign: 'middle',
};

export default AdminErrorLogs;
